import React, {useContext, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {UserHomepageControllerContext, UserHomepageStateContext} from "../../../../index";
import {deepEqual, preventEventPropagation} from "../../../../../../../core/services/utils/utils";
import {UserHomepageComponentExpandability, UserHomepageComponentTypes, UserHomepageEventEmitter} from "../../../../models";
import {Collapse} from "reactstrap";
import UserHomepageControlPanelComponentDatesSection from "./sections/dates";
import UserHomepageControlPanelComponentContents from "./sections/contents";
import UserHomepageControlPanelComponentHeaderInformation from "./sections/hedaer-info";
import UserHomepageControlPanelComponentHeader from "./sections/header";
import Api from "../../../../../../../core/services/api/api";
import useCanManageHomepage from "../../../../hooks/use-can-manage-homepage";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import UserHomepageUtils from "../../../../core/utils";


export const UserHomepageControlPanelComponent = ({tabId, component, sortable, dragging}) => {
    const controller = useContext(UserHomepageControllerContext);
    const state = useContext(UserHomepageStateContext);
    const [inEditMode, setInEditMode] = useState(false);
    const [editingTitle, setEditingTitle] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [removing, setRemoving] = useState(false);
    const isMounted = useIsMounted();
    const canManageHomepage = useCanManageHomepage();
    const {packages} = useAccessLevels();
    const hasHomepageManageFlag =
        packages.clientPlatform.manageWebHomepageEditions ||
        packages.clientPlatform.managePhoneHomepageEditions;
    const datesSectionRef = useRef();

    const currentComponent = useMemo(() => {
        const currentTab = state?.currentData?.tabs?.find(e => e.id === tabId);
        return currentTab?.components?.find(e => e.id === component.id);
    }, [component, tabId])

    const disabled = useMemo(() => removing || editingTitle || updating, [updating, removing, editingTitle]);
    const showDates = useMemo(() =>
            component?.expanded === UserHomepageComponentExpandability.dates &&
            ![UserHomepageComponentTypes.timeBased].includes(component.type?.id),
        [component?.expanded])
    const showContent = useMemo(() => component?.expanded === UserHomepageComponentExpandability.content, [component?.expanded])

    /**
     * Updates the title of the current component in the server.
     *
     * @param {string} title
     * @return {Promise<undefined | boolean>}
     */
    const updateComponentTitle = async (title) => {
        if (!canManageHomepage || !hasHomepageManageFlag) return;
        setEditingTitle(true);
        const forApi = {
            id: component?.id,
            title: title,
        }
        const response = await Api.updateHomepageEditionTabComponentGeneralInformation(forApi);
        if (!isMounted())
            return;
        setEditingTitle(false);
        return response?.resultFlag;
    }

    /**
     * Removes the current component from the home page edition.
     */
    const removeComponent = async () => {
        if (!canManageHomepage || !hasHomepageManageFlag) return;
        setRemoving(true);
        const response = await Api.removeHomepageEditionTabComponent(component.id);
        if (!isMounted())
            return;
        setRemoving(false);
        return response?.resultFlag;
    }

    /**
     * Updates the content of the current component in the server.
     *
     * * obtains the changes in the component to determine which of the update apis of the component must be called.
     * * calls any of the necessary apis for updating the information of the component
     * * only commits the changes if all the api calls have responded successfully.
     * @return {Promise<void>}
     */
    const updateComponent = async () => {
        if (!canManageHomepage || !hasHomepageManageFlag) return;
        setUpdating(true);
        const [generalInfoForApi, contentForApi] = getComponentChanges(currentComponent, component);

        let generalInfoResponse = {resultFlag: true};
        if (!!generalInfoForApi)
            generalInfoResponse = await Api.updateHomepageEditionTabComponentGeneralInformation(generalInfoForApi);

        let contentResponse = {resultFlag: true};
        if (!!contentForApi)
            contentResponse = await Api.updateHomepageEditionTabComponentContent(contentForApi);

        if (!isMounted())
            return;
        setUpdating(false);
        return contentResponse?.resultFlag && generalInfoResponse?.resultFlag;
    }

    /**
     * Determines the changes of the component and prepares the data needed for either of updating general info or
     * updating content of a component.
     * @param {UserHomepageComponent} prev
     * @param {UserHomepageComponent} curr
     * @return {[any,any]}
     */
    const getComponentChanges = (prev, curr) => {
        let generalInfoChanges = {};
        let contentChanges = {};
        const isTimeBased = curr?.type?.id === UserHomepageComponentTypes.timeBased;

        if (prev.startDate !== curr.startDate) {
            generalInfoChanges['startDate'] = curr.startDate ?? '';
            if (!curr.startDate) {
                generalInfoChanges['deleteStartDate'] = true;
            }
        }

        if (isTimeBased) {
            const prevFinishDate = UserHomepageUtils.getTimeBasedFinishDateTime(prev);
            const currFinishDate = UserHomepageUtils.getTimeBasedFinishDateTime(curr);
            if (prevFinishDate !== currFinishDate) {
                generalInfoChanges['endDate'] = currFinishDate ?? '';
                if (!currFinishDate) {
                    generalInfoChanges['deleteEndDate'] = true;
                }
            }
        } else if (prev.endDate !== curr.endDate) {
            generalInfoChanges['endDate'] = curr.endDate ?? '';
            if (!curr.endDate) {
                generalInfoChanges['deleteEndDate'] = true;
            }
        }

        if (prev.industryId !== curr.industryId) {
            generalInfoChanges['industryId'] = curr.industryId;
        }

        if (prev.searchType !== curr.searchType) {
            generalInfoChanges['searchTypeId'] = curr.searchType;
        }

        if (prev.nexusSearchId !== curr.nexusSearchId) {
            generalInfoChanges['nexusSearchId'] = curr.nexusSearchId;
        }

        if (!deepEqual(prev.searchQuery, curr.searchQuery)) {
            generalInfoChanges[`searchQuery_${curr.searchType}`] = curr.searchQuery;
        }

        const componentContentChanges = UserHomepageUtils.getComponentContentChanges(prev, curr);
        if (componentContentChanges) {
            Object.assign(contentChanges, componentContentChanges);
        }

        if (Object.keys(generalInfoChanges).length) generalInfoChanges['id'] = curr.id;
        else generalInfoChanges = undefined;

        if (Object.keys(contentChanges).length) contentChanges['id'] = curr.id;
        else contentChanges = undefined;

        return [generalInfoChanges, contentChanges]
    }

    /**
     * Invokes the appropriate controller callback for the click of the component card.
     */
    const onComponentClicked = () => {
        if (dragging || component.expanded !== UserHomepageComponentExpandability.none)
            return;
        controller.selectComponent(
            tabId,
            component.id,
            UserHomepageEventEmitter.controlPanel,
            UserHomepageComponentExpandability.content,
        );
    }

    /**
     * Calls the resetFields method of the datesSectionRef as soon as the component itself is removed.
     */
    const onComponentChangesReverted = () => {
        datesSectionRef.current?.resetFields();
    }

    const header = useMemo(() => (
        <UserHomepageControlPanelComponentHeader
            sortable={sortable}
            component={component}
            tabId={tabId}
            update={updateComponent}
            remove={removeComponent}
            inEditMode={inEditMode}
            currentComponent={currentComponent}
            onRevert={onComponentChangesReverted}
        />
    ), [inEditMode, sortable, component, tabId, currentComponent])

    const headerInformation = useMemo(() => (
        <UserHomepageControlPanelComponentHeaderInformation
            componentId={component.id}
            currentTitle={currentComponent.title}
            type={component.type}
            tabId={tabId}
            inEditMode={inEditMode}
            setInEditMode={setInEditMode}
            update={updateComponentTitle}
            disabled={disabled}
        />
    ), [component.id, currentComponent.title, inEditMode, component.type, disabled])

    const dates = useMemo(() => (
        <Collapse isOpen={showDates}>
            <div>
                {
                    showDates &&
                    <UserHomepageControlPanelComponentDatesSection
                        componentId={component.id}
                        type={component.type}
                        currentEndDate={currentComponent.endDate}
                        currentStartDate={currentComponent.startDate}
                        tabId={tabId}
                        endDateRemovable
                        ref={datesSectionRef}
                    />
                }
            </div>
        </Collapse>
    ), [showDates, component, currentComponent, tabId, datesSectionRef])

    const contents = useMemo(() => (
        <Collapse isOpen={showContent}>
            <div>
                {
                    showContent &&
                    <UserHomepageControlPanelComponentContents
                        tabId={tabId}
                        component={component}
                        dragging={dragging}
                        disabled={disabled}
                        currentComponent={currentComponent}
                        ref={datesSectionRef}
                    />
                }
            </div>
        </Collapse>
    ), [showContent, tabId, dragging, disabled, currentComponent, component, datesSectionRef])


    return (
        <>
            <div
                className={'user-homepage-control-panel-component'}
                id={component?.controlPanelElementId}
                onClick={onComponentClicked}
            >
                {header}
                {headerInformation}
                <div
                    className={'w-100 d-flex flex-column'}
                    onClick={preventEventPropagation}
                >
                    {dates}
                    {contents}
                </div>
            </div>
        </>
    );
}


export default UserHomepageControlPanelComponent;
